import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Header } from '@/components/layout/Header';
import { api } from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-store';
import type { Inbox } from '@shared/types';
import {
  LayoutDashboard,
  Mail,
  Trash2,
  ExternalLink,
  Plus,
  ShieldCheck,
  Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';
export function AdminDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const userName = useAuthStore((s) => s.user?.name);
  const { data: inboxesData, isLoading } = useQuery({
    queryKey: ['inboxes'],
    queryFn: () => api<{ items: Inbox[] }>('/api/inboxes'),
    enabled: isAuthenticated,
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api(`/api/inboxes/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inboxes'] });
      toast.success('Đã xóa hộp thư thành công.');
    }
  });
  const createMutation = useMutation({
    mutationFn: () => api<Inbox>('/api/inboxes', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inboxes'] });
      toast.success('Hộp thư mới đã được tạo.');
    }
  });
  const purgeMutation = useMutation({
    mutationFn: () => api<{ purged: number }>('/api/admin/purge', { method: 'POST' }),
    onSuccess: (data) => {
      toast.success(`Đã dọn dẹp thành công ${data.purged} email cũ.`);
    },
    onError: (err: any) => {
      toast.error(err.message || 'Dọn dẹp thất bại.');
    }
  });
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);
  if (!isAuthenticated) return null;
  const inboxes = inboxesData?.items ?? [];
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-8 md:py-10 lg:py-12">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <LayoutDashboard className="h-8 w-8 text-primary" />
                Bảng điều khiển Quản trị
              </h1>
              <p className="text-muted-foreground mt-1">
                Chào mừng quay trở lại, {userName ?? 'Quản trị viên'}.
              </p>
            </div>
            <Button
              onClick={() => createMutation.mutate()}
              className="btn-purple gap-2"
              disabled={createMutation.isPending}
            >
              <Plus className="h-4 w-4" />
              Tạo Inbox mới
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <Card className="glass-card border-white/10">
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-sm font-medium">Tổng số Inbox</CardTitle>
                <Mail className="h-4 w-4 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{inboxes.length}</div>
              </CardContent>
            </Card>
            <Card className="glass-card border-white/10">
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-sm font-medium">Trạng thái</CardTitle>
                <ShieldCheck className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">Đã bảo mật</div>
              </CardContent>
            </Card>
            <Card className="glass-card border-white/10">
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-sm font-medium">Bảo trì</CardTitle>
                <Zap className="h-4 w-4 text-yellow-500" />
              </CardHeader>
              <CardContent className="pb-2">
                <p className="text-xs text-muted-foreground">Xóa email cũ hơn 7 ngày.</p>
              </CardContent>
              <CardFooter>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => purgeMutation.mutate()}
                  disabled={purgeMutation.isPending}
                >
                  Dọn dẹp
                </Button>
              </CardFooter>
            </Card>
          </div>
          <div className="glass-card rounded-2xl overflow-hidden border border-white/10">
            <div className="px-6 py-4 border-b border-white/10 bg-white/5">
              <h3 className="font-bold">Quản lý Inbox</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-white/10 bg-white/5">
                    <th className="px-6 py-4 text-xs font-semibold text-muted-foreground uppercase">Địa chỉ</th>
                    <th className="px-6 py-4 text-xs font-semibold text-muted-foreground uppercase">Đã tạo</th>
                    <th className="px-6 py-4 text-xs font-semibold text-muted-foreground uppercase text-right">Hành động</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {isLoading ? (
                    <tr><td colSpan={3} className="px-6 py-12 text-center text-muted-foreground">Đang tải...</td></tr>
                  ) : inboxes.length === 0 ? (
                    <tr><td colSpan={3} className="px-6 py-12 text-center text-muted-foreground">Trống.</td></tr>
                  ) : (
                    inboxes.map((inbox) => (
                      <tr key={inbox.id} className="hover:bg-white/5 transition-colors">
                        <td className="px-6 py-4 font-mono text-primary">{inbox.address}</td>
                        <td className="px-6 py-4 text-sm text-muted-foreground">
                          {format(inbox.createdAt, 'PPp', { locale: vi })}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => navigate('/')}><ExternalLink className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="sm" className="text-destructive" onClick={() => deleteMutation.mutate(inbox.id)}><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}